import type { i18n as I18nInstance } from 'i18next';
import type LocalStorageBackend from 'i18next-localstorage-backend';
import {
  applyLocalStorageBackendVersion,
  invalidateLocalStorageBackendCache,
  readStoredVersion
} from './translationVersion';

const VERSION_PATH = 'version';

export type DashboardVersionResult = { ok: false } | { ok: true; hash: string | null };

export const fetchDashboardTranslationsVersion = async (): Promise<DashboardVersionResult> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${VERSION_PATH}`, { method: 'GET' });
    if (!response.ok) {
      return { ok: false };
    }
    const json = await response.json();
    const hash = json.dashboardTranslationsVersion;
    return { ok: true, hash };
  } catch {
    return { ok: false };
  }
};

type RefreshableI18n = Pick<I18nInstance, 'reloadResources'> &
  Partial<Pick<I18nInstance, 'changeLanguage' | 'language'>>;

const safeReloadAndRefreshReactBindings = async (i18n: RefreshableI18n | null | undefined): Promise<void> => {
  if (!i18n || typeof i18n.reloadResources !== 'function') return;
  try {
    await i18n.reloadResources();
  } catch {
    // i18next may not yet be initialized; reload failure is non-fatal
    return;
  }

  const currentLanguage = i18n.language;
  if (typeof currentLanguage !== 'string' || typeof i18n.changeLanguage !== 'function') return;

  try {
    // react-i18next listens to languageChanged by default; reloadResources only emits loaded.
    await i18n.changeLanguage(currentLanguage);
  } catch {
    // language refresh failure is non-fatal; the updated cache will be used on the next load
  }
};

export const reconcileTranslationCache = async (
  i18n: RefreshableI18n | null | undefined,
  localStorageBackend: LocalStorageBackend,
  fetcher: () => Promise<DashboardVersionResult> = fetchDashboardTranslationsVersion
): Promise<void> => {
  const result = await fetcher();

  if (!result?.ok || result.hash === null) {
    invalidateLocalStorageBackendCache(localStorageBackend);
    await safeReloadAndRefreshReactBindings(i18n);
    return;
  }

  const storedVersion = readStoredVersion();
  const serverVersion = result.hash;
  applyLocalStorageBackendVersion(localStorageBackend, serverVersion);

  if (storedVersion === serverVersion) return;
  await safeReloadAndRefreshReactBindings(i18n);
};
